import { useState } from 'react';
import { queryCollection, Issue } from '../lib/api';
import { 
  MagnifyingGlass, 
  CheckCircle, 
  Clock, 
  Wrench, 
  XCircle,
  House,
  WarningCircle,
  ArrowRight,
  Spinner
} from '@phosphor-icons/react';

// Issue type definition
interface IssueData {
  id: string;
  tracking_number?: string;
  title?: string;
  description?: string;
  category?: string;
  urgency?: number;
  urgency_level?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
  resolved_at?: string;
  started_at?: string;
  resolution_notes?: string;
}

// Public issue tracking page - no login required
export default function TrackIssuePage() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [issue, setIssue] = useState<IssueData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!trackingNumber.trim()) {
      setError('Ange ett ärendenummer');
      return;
    }
    
    setLoading(true);
    setError(null);
    setIssue(null);
    setSearched(true);
    
    try {
      // Search by tracking number (which is stored as id or tracking_number)
      const result = await queryCollection<Issue>('issues', { 
        tracking_number: trackingNumber.trim().toUpperCase() 
      }, { limit: 1 });
      
      if (result.length > 0) {
        setIssue(result[0]);
      } else {
        // Try searching by ID if tracking number not found
        const resultById = await queryCollection<Issue>('issues', { 
          id: trackingNumber.trim() 
        }, { limit: 1 });
        
        if (resultById.length > 0) {
          setIssue(resultById[0]);
        } else {
          setError('Inget ärende hittades med det angivna numret');
        }
      }
    } catch (err) {
      console.error('Failed to search issue:', err);
      setError('Kunde inte söka efter ärendet. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    const statuses: Record<string, { label: string; color: string; icon: typeof Clock; description: string }> = {
      open: { 
        label: 'Öppet', 
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Clock,
        description: 'Ärendet har registrerats och väntar på åtgärd'
      },
      in_progress: { 
        label: 'Pågår', 
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: Wrench,
        description: 'Ärendet hanteras just nu'
      },
      resolved: { 
        label: 'Löst', 
        color: 'bg-green-100 text-green-700 border-green-200',
        icon: CheckCircle,
        description: 'Ärendet har åtgärdats'
      },
      closed: { 
        label: 'Stängt', 
        color: 'bg-slate-100 text-slate-700 border-slate-200',
        icon: XCircle,
        description: 'Ärendet är avslutat'
      }
    };
    return statuses[status] || statuses.open;
  };

  const getUrgencyLabel = (level: number) => {
    const labels: Record<number, { label: string; color: string }> = {
      1: { label: 'Akut', color: 'text-red-600' },
      2: { label: 'Hög', color: 'text-orange-600' },
      3: { label: 'Normal', color: 'text-yellow-600' },
      4: { label: 'Låg', color: 'text-green-600' },
      5: { label: 'Planerad', color: 'text-slate-600' }
    };
    return labels[level] || labels[3];
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, string> = {
      water: 'Vatten/Avlopp',
      electrical: 'El/Belysning',
      heating: 'Värme/Ventilation',
      doors: 'Dörrar/Lås',
      outdoor: 'Utemiljö',
      elevator: 'Hiss',
      other: 'Övrigt'
    };
    return categories[category] || category;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <House size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Spåra ärende</h1>
              <p className="text-sm text-slate-500">Följ upp status på ditt felanmälan</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Ange ditt ärendenummer</h2>
          <p className="text-sm text-slate-500 mb-6">
            Du hittar ärendenumret i bekräftelsemailet du fick när ärendet registrerades.
          </p>
          
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass 
                size={20} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                placeholder="t.ex. ARN-2024-00123"
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <Spinner size={20} className="animate-spin" />
              ) : (
                <MagnifyingGlass size={20} />
              )}
              Sök
            </button>
          </form>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <WarningCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Issue Details */}
        {issue && (
          <div className="mt-6 space-y-4">
            {/* Status Card */}
            <div className={`p-6 rounded-xl border ${getStatusInfo(issue.status || 'open').color}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/50 flex items-center justify-center">
                  {(() => {
                    const StatusIcon = getStatusInfo(issue.status || 'open').icon;
                    return <StatusIcon size={28} />;
                  })()}
                </div>
                <div>
                  <div className="text-sm font-medium opacity-75">Status</div>
                  <div className="text-xl font-bold">{getStatusInfo(issue.status || 'open').label}</div>
                  <div className="text-sm mt-1">{getStatusInfo(issue.status || 'open').description}</div>
                </div>
              </div>
            </div>

            {/* Issue Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Ärendeinformation</h3>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-slate-500">Ärendenummer</div>
                  <div className="font-mono font-medium text-slate-900">
                    {issue.tracking_number || issue.id?.substring(0, 12).toUpperCase()}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-slate-500">Titel</div>
                  <div className="font-medium text-slate-900">{issue.title}</div>
                </div>
                
                {issue.description && (
                  <div>
                    <div className="text-sm text-slate-500">Beskrivning</div>
                    <div className="text-slate-700">{issue.description}</div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-500">Kategori</div>
                    <div className="text-slate-900">{getCategoryLabel(issue.category || 'other')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Prioritet</div>
                    <div className={getUrgencyLabel(issue.urgency_level || 3).color}>
                      {getUrgencyLabel(issue.urgency_level || 3).label}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-500">Registrerat</div>
                    <div className="text-slate-900">{formatDate(issue.created_at)}</div>
                  </div>
                  {issue.resolved_at && (
                    <div>
                      <div className="text-sm text-slate-500">Åtgärdat</div>
                      <div className="text-slate-900">{formatDate(issue.resolved_at)}</div>
                    </div>
                  )}
                </div>
                
                {issue.resolution_notes && (
                  <div className="pt-4 border-t border-slate-200">
                    <div className="text-sm text-slate-500 mb-2">Åtgärdsnotering</div>
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                      {issue.resolution_notes}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            {issue.status !== 'open' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Tidslinje</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Clock size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Ärendet registrerat</div>
                      <div className="text-sm text-slate-500">{formatDate(issue.created_at)}</div>
                    </div>
                  </div>
                  
                  {(issue.status === 'in_progress' || issue.status === 'resolved' || issue.status === 'closed') && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Wrench size={16} className="text-amber-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">Arbete påbörjat</div>
                        <div className="text-sm text-slate-500">{formatDate(issue.started_at || issue.updated_at)}</div>
                      </div>
                    </div>
                  )}
                  
                  {(issue.status === 'resolved' || issue.status === 'closed') && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={16} className="text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">Ärendet löst</div>
                        <div className="text-sm text-slate-500">{formatDate(issue.resolved_at || issue.updated_at)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Har du frågor?</h3>
              <p className="text-blue-800 text-sm">
                Kontakta din förvaltning om du har frågor om ditt ärende. 
                Ange alltid ditt ärendenummer när du kontaktar oss.
              </p>
            </div>
          </div>
        )}

        {/* No Results */}
        {searched && !issue && !loading && !error && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlass size={32} className="text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Inget ärende hittades</h3>
            <p className="text-slate-500 text-sm">
              Kontrollera att du angett rätt ärendenummer och försök igen.
            </p>
          </div>
        )}

        {/* Link to report new issue */}
        <div className="mt-8 text-center">
          <a 
            href="/report"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            Rapportera ett nytt fel
            <ArrowRight size={16} />
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 text-center">
        <p className="text-xs text-slate-400">Powered by Förvaltningen</p>
      </div>
    </div>
  );
}
